import { FeaturesCollection } from '@principle-theorem/feature-flags';
import {
  ACC_FUNDING_REQUESTS_FEATURE,
  ALL_FEATURES,
} from '@principle-theorem/principle-core/features';
import {
  IPractice,
  IStaffer,
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  Firestore,
  Region,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Brand } from '../../brand';
import { MohAgreementHolder } from '../../funding-request/moh/moh-agreement-holder';
import { Staffer } from '../../staffer/staffer';
import { Practice } from '../practice';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
} from './practice-setup-step.types';
import {
  initRawSchema,
  toListContent,
  toParagraphContent,
} from '@principle-theorem/editor';

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/brand/{brandId}/staff'],
  validationMode: 'count',
};

export const providerNumbersStepNZ: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.ProviderNumbers,
  category: PracticeSetupStepCategory.GeneralPracticeSettings,
  title: 'Set Up Practitioner Settings',
  description: initRawSchema([
    toParagraphContent(
      `Ensure all practitioners have relevant provider numbers and online profile settings set up. The following provider numbers will need to be filled in for claims to be processed correctly:`
    ),
    toListContent([
      'MOH Provider Settings',
      'ACC Provider Settings',
      'Prescriber Number (same as DCNZ number)',
      'Online Profile Settings',
    ]),
  ]),
  dependsOn: [PracticeSetupStepId.StaffSetup],
  regions: [Region.NewZealand],
  getRoute: (brand, _practice) => `settings/brand/${brand.ref.id}/staff`,
  validate$: (practice$, _step, _options) => {
    const organisation$ = practice$.pipe(
      switchMap((practice) =>
        Firestore.getDoc(
          Brand.organisationRef(asReffable(Practice.brandRef(practice)))
        )
      )
    );

    return combineLatest([practice$, organisation$]).pipe(
      switchMap(([practice, organisation]) =>
        Staffer.practitionersByPractice$(practice).pipe(
          map((practitioners) => {
            const missingPractitionerNames: string[] = [];
            const totalPractitioners = practitioners.length;

            for (const practitioner of practitioners) {
              if (
                !hasRequiredProviderFields(
                  practitioner,
                  practice,
                  organisation.optInFeatures ?? []
                )
              ) {
                missingPractitionerNames.push(
                  practitioner.user?.name || 'Unknown'
                );
              }
            }

            if (missingPractitionerNames.length > 0) {
              const completedCount =
                practitioners.length - missingPractitionerNames.length;

              return {
                valid: false,
                reason: `Completed ${completedCount}/${
                  practitioners.length
                } practitioners. Missing provider details for: \r\n${missingPractitionerNames
                  .sort()
                  .map((name) => `- ${name}`)
                  .join('\r\n')}`,
                progress: {
                  current: completedCount,
                  total: totalPractitioners,
                },
              };
            }

            return { valid: true };
          })
        )
      )
    );
  },
  pageVisitTracking,
};

function hasRequiredProviderFields(
  practitioner: IStaffer,
  practice: WithRef<IPractice>,
  optInFeatures: string[]
): boolean {
  const nzDetails = practitioner.metadata?.nz?.providerDetails;
  const accEnabled = new FeaturesCollection(
    ALL_FEATURES,
    true
  ).isFeatureEnabled(ACC_FUNDING_REQUESTS_FEATURE, optInFeatures);
  const accFeatures = accEnabled
    ? [
        nzDetails?.acc?.providerId?.trim(),
        nzDetails?.acc?.providerTypeCode?.trim(),
      ]
    : [];

  const claimsUnderPractice = MohAgreementHolder.claimsUnderPractice(practice);

  const agreementHolderNumbers = claimsUnderPractice
    ? []
    : practitionerAgreementHolderNumbers(practitioner, practice);

  const requiredFields = [
    ...accFeatures,
    ...agreementHolderNumbers,
    nzDetails?.dcnzNumber?.trim(),
    practitioner.prescriberNumber?.trim(),
  ];
  return requiredFields.every((field) => !!field);
}

function practitionerAgreementHolderNumbers(
  practitioner: IStaffer,
  practice: WithRef<IPractice>
): string[] {
  const agreementHolder = MohAgreementHolder.fromPractitioner(
    practitioner,
    practice
  );
  return [
    agreementHolder.payeeNumber.trim(),
    agreementHolder.agreementNumber.trim(),
  ];
}
